import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { asArray, asRecord, runDomainRouterQuery } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type StudentsFamiliesDashboardData = {
  items: Record<string, unknown>[];
  totalCount: number;
  nextToken: string | null;
};

type FamilyDetailData = {
  family: Record<string, unknown> | null;
  balance: Record<string, unknown> | null;
  transactions: Record<string, unknown>[];
  communications: Record<string, unknown>[];
};

type FamilyDetailEntry = {
  status: AsyncStatus;
  error: string | null;
  data: FamilyDetailData | null;
};

type StudentsFamiliesLiveState = {
  dashboard: {
    status: AsyncStatus;
    error: string | null;
    data: StudentsFamiliesDashboardData;
  };
  detailByFamilyId: Record<string, FamilyDetailEntry>;
};

const initialState: StudentsFamiliesLiveState = {
  dashboard: {
    status: 'idle',
    error: null,
    data: {
      items: [],
      totalCount: 0,
      nextToken: null,
    },
  },
  detailByFamilyId: {},
};

const createEmptyDetail = (): FamilyDetailEntry => ({
  status: 'idle',
  error: null,
  data: null,
});

export const fetchStudentsFamiliesDashboard = createAsyncThunk(
  'studentsFamiliesLive/fetchDashboard',
  async (input: Record<string, unknown> = {}): Promise<StudentsFamiliesDashboardData> => {
    const data = asRecord(
      await runDomainRouterQuery('listFamilies', {
        input: JSON.stringify(input),
      })
    );

    return {
      items: asArray<Record<string, unknown>>(data.items),
      totalCount: Number(data.totalCount ?? 0),
      nextToken: data.nextToken == null ? null : String(data.nextToken),
    };
  }
);

export const fetchStudentsFamilyDetail = createAsyncThunk(
  'studentsFamiliesLive/fetchFamilyDetail',
  async ({ familyId, academicYear }: { familyId: string; academicYear?: string }) => {
    const normalizedFamilyId = String(familyId ?? '').trim();
    const effectiveAcademicYear = String(academicYear ?? '2026-2027').trim();

    const [familyRaw, balanceRaw, transactionsRaw, communicationsRaw] = await Promise.all([
      runDomainRouterQuery('getFamily', { familyId: normalizedFamilyId }),
      runDomainRouterQuery('getFamilyBalance', {
        familyId: normalizedFamilyId,
        academicYear: effectiveAcademicYear,
      }),
      runDomainRouterQuery('listFamilyAccountTransactions', {
        familyId: normalizedFamilyId,
        academicYear: effectiveAcademicYear,
      }),
      runDomainRouterQuery('listCommunications', {
        familyId: normalizedFamilyId,
        limit: 200,
      }),
    ]);

    const transactionsData = asRecord(transactionsRaw);
    const communicationsData = asRecord(communicationsRaw);

    return {
      familyId: normalizedFamilyId,
      detail: {
        family: asRecord(familyRaw),
        balance: asRecord(balanceRaw),
        transactions: asArray<Record<string, unknown>>(transactionsData.items),
        communications: asArray<Record<string, unknown>>(communicationsData.items),
      } satisfies FamilyDetailData,
    };
  }
);

const studentsFamiliesLiveSlice = createSlice({
  name: 'studentsFamiliesLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudentsFamiliesDashboard.pending, (state) => {
        state.dashboard.status = 'loading';
        state.dashboard.error = null;
      })
      .addCase(fetchStudentsFamiliesDashboard.fulfilled, (state, action: PayloadAction<StudentsFamiliesDashboardData>) => {
        state.dashboard.status = 'succeeded';
        state.dashboard.data = action.payload;
      })
      .addCase(fetchStudentsFamiliesDashboard.rejected, (state, action) => {
        state.dashboard.status = 'failed';
        state.dashboard.error = action.error.message ?? 'Failed to load students/families dashboard.';
      })
      .addCase(fetchStudentsFamilyDetail.pending, (state, action) => {
        const familyId = action.meta.arg.familyId;
        const entry = state.detailByFamilyId[familyId] ?? createEmptyDetail();
        entry.status = 'loading';
        entry.error = null;
        state.detailByFamilyId[familyId] = entry;
      })
      .addCase(fetchStudentsFamilyDetail.fulfilled, (state, action) => {
        const { familyId, detail } = action.payload;
        state.detailByFamilyId[familyId] = {
          status: 'succeeded',
          error: null,
          data: detail,
        };
      })
      .addCase(fetchStudentsFamilyDetail.rejected, (state, action) => {
        const familyId = action.meta.arg.familyId;
        const entry = state.detailByFamilyId[familyId] ?? createEmptyDetail();
        entry.status = 'failed';
        entry.error = action.error.message ?? 'Failed to load family detail.';
        state.detailByFamilyId[familyId] = entry;
      });
  },
});

export const studentsFamiliesLiveReducer = studentsFamiliesLiveSlice.reducer;
